/* eslint-disable no-use-before-define */
import Vue from 'vue';
import Router from 'vue-router';

// Entry point
import Login from '@/pages/Login/Login';
import ExternalLogin from '@/pages/ExternalLogin/ExternalLogin';

/**
 * Prefetched modules to optimize UX (commonly used routes)
 * Reference: https://medium.com/webpack/link-rel-prefetch-preload-in-webpack-51a52358f84c
 */
const Module = () =>
  import(
    /* webpackPrefetch: true, webpackChunkName: "page_module" */ '@/pages/Module/Module'
  );
const Dashboard = () =>
  import(
    /* webpackPrefetch: true, webpackChunkName: "page_dashboard" */ '@/pages/Dashboard/Dashboard'
  );

const Style = () =>
  import(/* webpackChunkName: "page_style" */ '@/pages/Style/Style');
const Notfound = () =>
  import(/* webpackChunkName: "page_notfound" */ '@/pages/Notfound/Notfound');
const Disabled = () =>
  import(/* webpackChunkName: "page_disabled" */ '@/pages/Disabled/Disabled');
const Blocked = () =>
  import(/* webpackChunkName: "page_blocked" */ '@/pages/Blocked/Blocked');
const ChangePassword = () =>
  import(
    /* webpackChunkName: "page_changepassword" */ '@/pages/ChangePassword/ChangePassword'
  );
const Register = () =>
  import(/* webpackChunkName: "page_register" */ '@/pages/Register/Register');
const DirectScript = () =>
  import(
    /* webpackChunkName: "page_directscript" */ '@/pages/DirectScript/DirectScript'
  );
const Menu = () =>
  import(/* webpackChunkName: "page_menu" */ '@/pages/Menu/Menu');
const SingleScript = () =>
  import(
    /* webpackChunkName: "page_script" */ '@/pages/SingleScript/SingleScript'
  );
const StandaloneApp = () =>
  import(
    /* webpackChunkName: "page_standaloneapp" */ '@/pages/StandaloneApp/StandaloneApp'
  );
const Playground = () =>
  import(
    /* webpackChunkName: "page_playground" */ '@/pages/Playground/Playground'
  );
const Liveshop = () =>
  import(
    /* webpackChunkName: "page_liveshop" */ '@modules/live-shop/customerb2c.vue'
  );
const TermsConditions = () =>
  import(
    /* webpackChunkName: "terms_conditions" */ '@/pages/TermsConditions/TermsConditions'
  );
const Welcome = () =>
  import(/* webpackChunkName: "page_welcome" */ '@/pages/Welcome/Welcome');
const ThankYou = () =>
  import(/* webpackChunkName: "page_thank_you" */ '@/pages/Welcome/ThankYou');
const LeadCaptureDynamicForm = () =>
  import(
    /* webpackChunkName: "page_thank_you" */ '@/pages/Welcome/LeadCaptureDynamicForm'
  );
const NotificationsAcceptance = () =>
  import(
    /* webpackChunkName: "page_thank_you" */ '@/pages/NotificationsAcceptance/NotificationsAcceptance'
  );
const EntitiesValidation = () =>
  import(
    /* webpackChunkName: "page_entities_validation" */ '@/pages/EntitiesValidation/EntitiesValidation'
  );
const DynamicForm = () =>
  import(
    /* webpackChunkName: "page_dynamicform" */ '@/pages/DynamicForm/DynamicForm'
  );
const Help = () =>
  import(/* webpackChunkName: "page_help" */ '@/pages/Help/Help');
const FederationLogin = () =>
  import(
    /* webpackChunkName: "page_federation_login" */ '@/pages/FederationLogin/FederationLogin'
  );

Vue.use(Router);

const router = new Router({
  routes: [
    {
      path: '*',
      redirect: '/',
    },
    {
      path: '/',
      name: 'Login',
      component: Login,
    },
    {
      path: '/termos/:id/:version',
      name: 'termsConditions',
      component: TermsConditions,
    },
    {
      path: '/session/:code',
      name: 'ExternalLogin',
      component: ExternalLogin,
    },
    {
      path: '/style',
      name: 'Style',
      component: Style,
    },
    {
      path: '/404',
      name: '404',
      component: Notfound,
    },
    {
      path: '/changePassword',
      name: 'ChangePassword',
      component: ChangePassword,
    },
    {
      path: '/register',
      name: 'Register',
      component: Register,
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: Dashboard,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: ':moduleId/:itemId?',
          name: 'Module_Dashboard',
          component: Module,
        },
      ],
    },
    {
      path: '/directScript',
      name: 'DirectScript',
      component: DirectScript,
    },
    {
      path: '/menu',
      name: 'Menu',
      component: Menu,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: ':moduleId/:itemId?',
          name: 'Module_Menu',
          component: Module,
        },
      ],
    },
    {
      path: '/script',
      name: 'SingleScript',
      component: SingleScript,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: ':moduleId/:itemId?',
          name: 'Module_Script',
          component: Module,
        },
      ],
    },
    {
      path: '/app',
      name: 'StandaloneApp',
      component: StandaloneApp,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: ':moduleId/:itemId?',
          name: 'Module_StandaloneApp',
          component: Module,
        },
      ],
    },
    {
      path: '/playground',
      name: 'Playground',
      component: Playground,
    },
    {
      path: '/disabled',
      name: 'Disabled',
      component: Disabled,
      beforeEnter: (to, from, next) => {
        /**
         * Se o app não estiver desabilitado,
         * redireciona para a página de login
         */
        if (!isAppDisabled()) return next('/');
        return next();
      },
    },
    {
      path: '/blocked',
      name: 'Blocked',
      component: Blocked,
      beforeEnter: (to, from, next) => {
        /**
         * Se o dominio não estiver bloqueado,
         * redireciona para a página de login
         */
        if (!isAppDomainBlocked(to.query)) return next('/');

        return next();
      },
    },
    {
      path: '/(aovivo|live)/:id',
      name: 'LiveshopB2C_A',
      component: Liveshop,
      meta: {
        publicRoute: true,
      },
      props: (route) => ({ publicRouteId: route.params.id }),
    },
    {
      path: '/(aovivo|live)-*',
      name: 'LiveshopB2C_B',
      component: Liveshop,
      meta: {
        publicRoute: true,
      },
      props: (route) => ({
        publicRouteId: route.params[1] || route.params.pathMatch,
      }),
    },
    {
      path: '/welcome',
      name: 'Welcome',
      component: Welcome,
    },
    {
      path: '/welcome/thankyou',
      name: 'ThankYou',
      component: ThankYou,
    },
    {
      path: '/welcome/form/:hash',
      name: 'Form',
      component: LeadCaptureDynamicForm,
    },
    {
      path: '/notifications',
      name: 'Aceite de notificações',
      component: NotificationsAcceptance,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/entities-validation',
      name: 'Validações de Entidades',
      component: EntitiesValidation,
      meta: {
        requiresAuth: false,
      },
    },
    {
      path: '/dynamic-form/:hash',
      name: 'DynamicForm',
      component: DynamicForm,
      meta: {
        requiresAuth: false,
      },
    },
    {
      path: '/help',
      name: 'Help',
      component: Help,
      meta: {
        hybridRoute: true,
      },
    },
    {
      path: '/federationLogin/:appId*',
      name: 'federationLogin',
      component: FederationLogin,
      meta: {
        requiresAuth: false,
      },
    },
  ],
  mode: 'history',
});

const isAppBlocked = async (to, from, next) => {
  /**
   * REDIRECIONAMENTO CASO A APLICAÇÃO TENHA UMA MENSAGEM CUSTOMIZADA DE BLOQUEIO PARA A URL USADO ATUALMENTE
   */
  if (isAppDomainBlocked(to.query) && !brain.routerOnceEvaluated.blocked) {
    brain.routerOnceEvaluated.blocked = true;

    next({ path: '/blocked', query: to.query });
    return true;
  }

  /** Utilizado para garantir que não irá dar problemas com a QueryString ?dev=1 */
  if (!isAppDomainBlocked(to.query) && !brain.routerOnceEvaluated.blocked)
    brain.routerOnceEvaluated.blocked = true;

  return false;
};

const redirectIfIsAppDisabled = async (to, from, next) => {
  /**
   * REDIRECIONAMENTO CASO A APLICAÇÃO ESTEJA DESABILITADA
   */
  if (isAppDisabled() && to.name !== 'Disabled') {
    next('/disabled');
    return true;
  }

  return false;
};

const isNotProtectedRoute = async (to, from, next) => {
  /** Se a rota não for protegida, burla a etapa de validação de sessão */
  if (
    !to.matched.some((record) => record.meta.requiresAuth) &&
    to.path !== '/'
  ) {
    next();
    return true;
  }

  return false;
};

const handleHybridRoute = async (next) => {
  try {
    await brain.user.loginByToken();
    next();
    return true;
  } catch (err) {
    next();
    return true;
  }
};

const isHybridRoute = async (to) =>
  to.matched.some((record) => record.meta.hybridRoute) && to.path !== '/';

const isPublicRoute = async (to, from, next) => {
  /** Se a rota for isolada "deixa passar" */
  if (to.matched.some((record) => record.meta.publicRoute)) {
    await brain.user.loginPublic();
    next();
    return true;
  }

  return false;
};

const isLoginRouteWithoutAuth = async (to, from, next) => {
  /** Verifica se não possui um cookie de sessão e foi redirecionado ao login */
  if (!brain.cookies.get('code') && to.path === '/') {
    next();
    return true;
  }

  return false;
};

const isAuthenticatedAndAccessingModule = async (to, from, next) => {
  /**
   * Caso esteja acessando um módulo já tendo realizado o login, não precisa validar sessão.
   */
  if (to.name && to.name.includes('Module') && brain.user.isLogged()) {
    /** Fecha scripts @legacy abertos */
    brain.closeScript();

    next();

    return true;
  }

  return false;
};

router.beforeEach(async (to, from, next) => {
  // console.log(moment().format(), '- beforeEach - to', to);
  // console.log(moment().format(), '- beforeEach - from', from);

  /**
   * Fecha todos os modais abertos ao trocar de rota,
   * evitando que mantenha o modal travando a tela
   */
  brain.closeAllModals();

  /**
   * Caso uma das validações sejam verdadeiras não deve ir para as proximas validações,
   * pois já realizou um 'next'
   * @important - todas as funções colocadas nessa sequencia devem dar next() ou retornar false!
   */
  if (await isPublicRoute(to, from, next)) return;
  if (await isAuthenticatedAndAccessingModule(to, from, next)) return;
  if (await isAppBlocked(to, from, next)) return;
  if (await redirectIfIsAppDisabled(to, from, next)) return;
  if (await isLoginRouteWithoutAuth(to, from, next)) return;
  if (await isHybridRoute(to)) return handleHybridRoute(next);
  if (await isNotProtectedRoute(to, from, next)) return;

  /** Fecha scripts @legacy abertos */
  brain.closeScript();

  try {
    if (!brain.user.isLogged()) {
      brain.portal.isLoaded = false;

      await brain.user.loginByToken();

      /** Abre o chat após o login */
      brain.openChat();

      brain.portal.isLoaded = true;
    } else {
      await brain.user.validateSession();
    }

    /**
     * REDIRECIONAMENTO CASO EXISTA UM ACESSO JÁ COM TOKEN
     */
    if (to.path === '/' && from.path === '/') return brain.sendToAppRoute();
    // Corrige um bug com duplicação de navegação (por ex: estar em dash e ir para dash)
    if (to.path === '/') return next(false);
  } catch (err) {
    brain.sessionError(to.fullPath);
  }

  return next();
});

/** Utilities */
const isAppDisabled = () => _.get(brain, 'company.json.status') === 'disabled';
const isAppDomainBlocked = (query) => {
  const domainOpened = _.get(brain, 'company.domain');
  return (
    _.get(brain, `company.json.message.blockedAccess['${domainOpened}']`) &&
    query.dev !== '1'
  );
};

export default router;
